import queryString from "query-string";
import * as Sfx from "redux-saga/effects";

import {pureFetch} from "@atg-shared/fetch";
import {
    setGroups,
    setHighlightedGroups,
    setListViewGroups,
} from "@atg-sport-shared/sportsbook-data-access-group";
import {
    fetchKambiDetails,
    selectKambiDetails,
} from "@atg-sport-shared/sportsbook-data-access-kambi-details";
import {
    kambiGroupsParser,
    kambiHighlightedParser,
    kambiLeaguesParser,
} from "@atg-sport-shared/sportsbook-utils-parsers";

import {
    type GetLeagueAction,
    OFFERING_GET_GROUPS,
    OFFERING_GET_LEAGUE,
    OFFERING_GET_HIGHLIGHTED,
    offeringFetchFail,
    highlightsSuccess,
} from "./offering.actions";

import type {KambiDetailsResponse} from "@atg-sport-shared/sportsbook-types-kambi";

export const listViewBasePath = `listView/all/all/all/all`;

export const generateOfferingUrl = (
    service: string,
    kambiDetails: KambiDetailsResponse,
    params: {[key: string]: string | boolean} = {},
): string => {
    const {offering, market, clientId, locale, channelId, kambiOfferingUrl} =
        kambiDetails;

    const queryParams = queryString.stringify({
        ...params,
        lang: locale,
        market,
        client_id: clientId,
        channel_id: channelId,
        ncid: Date.now(),
    });
    return `${kambiOfferingUrl}/offering/v2018/${offering}/${service}?${queryParams}`;
};

export const SERVICE = {
    GROUPS: "GROUPS",
    LIVE_EVENTS: "LIVE_EVENTS",
    LIST_VIEW: "LIST_VIEW",
    HIGHLIGHTED: "HIGHLIGHTED",
    STARTING_WITHIN: "STARTING_WITHIN",
};

export const fetchKambiData = (
    service: string,
    kambiDetails: KambiDetailsResponse,
    params: {[key: string]: string} = {},
): Promise<any> => {
    const url = generateOfferingUrl(service, kambiDetails, params);
    return pureFetch(url, {method: "GET"});
};

export function* getGroupsFlow(): Generator<Sfx.Effect, void, any> {
    try {
        const kambiDetails = yield Sfx.select(selectKambiDetails);

        const groupData = yield Sfx.call(fetchKambiData, "group.json", kambiDetails);

        const parsedGroups = kambiGroupsParser(groupData.data.group.groups);

        yield Sfx.put(setGroups(parsedGroups));
    } catch (e: unknown) {
        yield Sfx.put(
            offeringFetchFail(
                SERVICE.GROUPS,
                e as {
                    [key: string]: string;
                },
            ),
        );
    }
}

export function* getLeagueFlow(
    action: GetLeagueAction,
): Generator<Sfx.Effect, void, any> {
    const kambiDetails = yield Sfx.select(selectKambiDetails);

    try {
        const leagueData = yield Sfx.call(
            fetchKambiData,
            `listView/${action.payload}.json`,
            kambiDetails,
        );

        const parsedGroups = kambiLeaguesParser(leagueData.data);

        yield Sfx.put(setListViewGroups(parsedGroups));
    } catch (e: unknown) {
        yield Sfx.put(
            offeringFetchFail(
                SERVICE.LIST_VIEW,
                e as {
                    [key: string]: string;
                },
            ),
        );
    }
}

export function* getHighlightedFlow(): Generator<Sfx.Effect, void, any> {
    const kambiDetails = yield Sfx.select(selectKambiDetails);

    try {
        const highlightedData = yield Sfx.call(
            fetchKambiData,
            "group/highlight.json",
            kambiDetails,
        );

        const parsedHighlightedGroups = kambiHighlightedParser(
            highlightedData.data.groups,
        );

        yield Sfx.put(setHighlightedGroups(parsedHighlightedGroups));
        yield Sfx.put(highlightsSuccess());
    } catch (e: unknown) {
        yield Sfx.put(
            offeringFetchFail(
                SERVICE.HIGHLIGHTED,
                e as {
                    [key: string]: string;
                },
            ),
        );
    }
}

export function* offeringSaga(): Generator<Sfx.Effect, void, any> {
    try {
        yield Sfx.put(fetchKambiDetails());

        yield Sfx.all([
            Sfx.takeLatest(OFFERING_GET_GROUPS, getGroupsFlow),
            Sfx.takeLatest(OFFERING_GET_HIGHLIGHTED, getHighlightedFlow),
            Sfx.takeLatest(OFFERING_GET_LEAGUE, getLeagueFlow),
        ]);
    } catch (error: unknown) {
        console.error("Error in offeringSaga:", error);
    }
}
